import { Controller } from "@hotwired/stimulus";

// The sheet of the collection menu (treasury plan, L2.6, L2.7): ticked lines
// add up to the total; the fields of the chosen payment method show, the
// others hide. The server checks it all.
//
// A ticked fee turns green and offers its choice (owner's request of 02/10):
// the whole of what is left, or a part — then the amount is typed, digits
// only, never above what is left, and what will remain counts down.
//
// One formula per optional service (owner's decision of 03/10): the boxes
// sharing a `data-exclusive-key` behave as radios — ticking one unticks the
// others. The server refuses two of them all the same.
//
// A required field the browser cannot point at — a select dressed by
// SlimSelect, the file of a dropzone — used to block the form with no
// message at all (QA3 CC-02): what is missing is then said in `problem`.
export default class extends Controller {
  static targets = ["row", "line", "amount", "choice", "total", "dock", "dockTotal", "nothingTicked", "submit", "method",
    "methodFields", "problem"];
  static values = { currency: { type: String, default: "F CFA" } };

  connect() {
    this.sayInvalid = this.sayInvalid.bind(this);
    // `invalid` does not bubble: caught on its way down.
    this.element.addEventListener("invalid", this.sayInvalid, true);
    this.refresh();
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.sayInvalid, true);
  }

  // The first field refused, named with the browser's own words.
  sayInvalid(event) {
    if (!this.hasProblemTarget || !this.problemTarget.hidden) return;

    const field = event.target;
    const label = field.closest(".ds-field")?.querySelector(".ds-field__label")?.textContent?.replace("*", "").trim();
    this.problemTarget.textContent = [label, field.validationMessage].filter(Boolean).join(" : ");
    this.problemTarget.hidden = false;
    this.problemTarget.scrollIntoView({ block: "nearest" });
  }

  refresh(event) {
    if (this.hasProblemTarget) this.problemTarget.hidden = true;
    this.keepOneFormula(event?.target);
    if (this.hasTotalTarget) this.refreshLines();
    this.refreshMethod();
  }

  // The box just ticked wins over the others of its service.
  keepOneFormula(changed) {
    const key = changed?.dataset?.exclusiveKey;
    if (!key || !changed.checked) return;

    this.lineTargets.forEach((box) => {
      if (box !== changed && box.dataset.exclusiveKey === key) box.checked = false;
    });
  }

  refreshLines() {
    let total = 0;
    this.rowTargets.forEach((row) => { total += this.refreshRow(row); });
    const ticked = this.rowTargets.some((row) => row.dataset.selected === "true");
    this.totalTarget.textContent = this.money(total);
    // The payment waits for a fee to be ticked; under the wide layout, the
    // total then follows the screen.
    if (this.hasNothingTickedTarget) this.nothingTickedTarget.hidden = ticked;
    this.submitTargets.forEach((button) => { button.disabled = !ticked; });
    if (this.hasDockTarget) {
      this.dockTarget.hidden = !ticked;
      this.dockTotalTarget.textContent = this.money(total);
    }
  }

  // One fee: its box, its choice, its amount. Returns what it adds to the total.
  refreshRow(row) {
    const box = row.querySelector('[data-collection-sheet-target="line"]');
    const choice = row.querySelector('[data-collection-sheet-target="choice"]');
    const amount = row.querySelector('[data-collection-sheet-target="amount"]');
    if (!box) return 0;

    box.disabled = box.dataset.locked === "true";
    const ticked = box.checked && !box.disabled;
    row.dataset.selected = ticked;
    if (!choice || !amount) return 0;

    choice.hidden = !ticked;
    const max = Number(amount.dataset.max);
    const partialBox = choice.querySelector('[data-part="partial"]');
    const partial = ticked && Boolean(partialBox) && partialBox.checked;
    const fields = choice.querySelector("[data-partial]");
    if (fields) fields.hidden = !partial;
    choice.querySelectorAll("input[type=radio]").forEach((radio) => { radio.disabled = !ticked; });
    amount.disabled = !ticked;
    amount.required = partial;
    const reason = choice.querySelector("[data-reason]");
    if (reason) reason.disabled = !partial;

    if (!partial) {
      amount.value = max;
      delete amount.dataset.touched;
      // Back to the whole amount (or unticked): a refusal left by the
      // partial choice no longer holds — kept, it silently blocked the form
      // (07/10).
      amount.setCustomValidity("");
      return ticked ? max : 0;
    }
    // Entering the partial choice: the cashier types the amount.
    if (!amount.dataset.touched) {
      amount.dataset.touched = "true";
      amount.value = "";
      amount.focus();
    }
    const digits = amount.value.replace(/\D/g, "");
    const value = Math.min(Number(digits || 0), max);
    amount.value = digits === "" ? "" : String(value);
    this.say(choice.querySelector("[data-left-after]"), amount, value, max);
    return value;
  }

  // Under the amount, as the retired « Nouvelle transaction » assistant did: what
  // will remain, counting down as it is typed — or why the amount is refused.
  say(hint, amount, value, max) {
    const min = Number(amount.dataset.min || 0);
    let refusal = "";
    if (value <= 0) refusal = hint.dataset.positive;
    else if (value < min) refusal = hint.dataset.minimum;
    else if (value >= max) refusal = hint.dataset.whole;
    amount.setCustomValidity(refusal);
    hint.style.color = refusal ? "var(--g4s-danger)" : "var(--ds-accent)";
    if (hint.frame) cancelAnimationFrame(hint.frame);
    if (refusal) {
      hint.textContent = refusal;
      delete hint.dataset.value;
      return;
    }
    const target = max - value;
    const from = Number(hint.dataset.value ?? max);
    hint.dataset.value = target;
    const show = (left) => { hint.textContent = hint.dataset.valid.replace("%{amount}", this.money(left)); };
    if (from === target || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      show(target);
      return;
    }
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / 250, 1);
      show(Math.round(from + ((target - from) * progress)));
      if (progress < 1) hint.frame = requestAnimationFrame(step);
    };
    hint.frame = requestAnimationFrame(step);
  }

  money(amount) {
    return `${new Intl.NumberFormat("fr-FR").format(amount)} ${this.currencyValue}`;
  }

  refreshMethod() {
    if (!this.hasMethodTarget) return;

    const method = this.methodTarget.value;
    this.methodFieldsTargets.forEach((block) => {
      const shown = block.dataset.method === method;
      block.hidden = !shown;
      block.querySelectorAll("input, select").forEach((field) => { field.disabled = !shown; });
    });
  }
}
